import React, { useState, useRef, useEffect } from 'react';
import { Input } from './input';
import { useTypesenseSearch } from '../../hooks/useTypesenseSearch';
import { TypesenseUser } from '../../lib/typesense/types';
import { Search, User } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

interface UserSearchAutocompleteProps {
  onUserSelect: (user: TypesenseUser) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  testId?: string;
}

export const UserSearchAutocomplete: React.FC<UserSearchAutocompleteProps> = ({
  onUserSelect,
  placeholder,
  disabled = false,
  className = "",
  inputClassName = "",
  testId,
}) => {
  const { t } = useTranslation('diagram');
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { users, isLoading, error, search, clearResults } = useTypesenseSearch({
    debounceMs: 100,
    minQueryLength: 2,
    maxResults: 5,
  });

  // Handle search when query changes
  useEffect(() => {
    if (query.trim()) {
      search(query);
      setIsOpen(true);
    } else {
      clearResults();
      setIsOpen(false);
    }
  }, [query, search, clearResults]);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleUserSelect = (user: TypesenseUser) => {
    onUserSelect(user);
    setQuery('');
    setIsOpen(false);
    clearResults();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && query.trim()) {
      e.preventDefault();
      const email = query.trim();
      const matchingUser = users.find(user => user.email.toLowerCase() === email.toLowerCase());
      if (matchingUser) {
        handleUserSelect(matchingUser);
        return;
      }

      // Allow adding non-org users only when the typed value is an email.
      if (!/^\S+@\S+\.\S+$/.test(email)) return;

      const emailUser: TypesenseUser = {
        id: `email-${email}`,
        ID: `email-${email}`,
        email,
        first_name: '',
        last_name: '',
      };
      handleUserSelect(emailUser);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };


  return (
    <div className={`relative ${className}`}>

      {/* Search input */}
      <div className="relative">
        <Input
          ref={inputRef}
          data-testid={testId}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onFocus={() => query.trim() && setIsOpen(true)}
          placeholder={placeholder ?? t('ui.searchUsers')}
          disabled={disabled}
          className={`pr-10 ${inputClassName}`}
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          {isLoading ? (
            <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
          ) : (
            <Search className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Dropdown with search results */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {error && (
            <div className="p-3 text-sm text-destructive bg-destructive/10">
              {error}
            </div>
          )}
          
          {users.length === 0 && !isLoading && query.trim() && !error && (
            <div className="p-3 text-sm text-muted-foreground">
              {t('ui.noUsersFound', { query })}
              <div className="text-xs mt-1">
                {t('ui.pressEnterToAdd', { query })}
              </div>
            </div>
          )}
          
          {users.map(user => (
            <button
              key={user.id}
              data-testid={testId ? `${testId}.option.${user.id}` : undefined}
              className="w-full text-left p-3 hover:bg-muted/50 flex items-center gap-3"
              onClick={() => handleUserSelect(user)}
              disabled={false}
            >
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {user.first_name && user.last_name 
                  ? `${user.first_name} ${user.last_name}` 
                  : user.email}
              </div>
              {user.first_name && user.last_name && user.email && (
                <div className="text-xs text-muted-foreground truncate">
                  {user.email}
                </div>
              )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
